import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { absoluteUrl, currentUrlLang, hreflangLinks, isPublishedInEnglish } from '@/i18n/urlLanguage';

/**
 * SEO Component - Manages meta tags, canonical URLs, indexing directives and
 * page-level JSON-LD. Production domain: https://ridalo.co
 *
 * Titles/descriptions are passed already translated by the page (t()), so the
 * metadata always matches the language actually displayed. The static HTML
 * served before JavaScript runs is generated at build time
 * (scripts/seo-static.js) from the same `seo.*` translation keys.
 */
export const SITE_URL = 'https://ridalo.co';
// Share preview (WhatsApp, Facebook, X…): official logo + « Ridalo » side by side on black, 1200 × 630 —
// same file as the static og:image in public/index.html.
const DEFAULT_IMAGE = `${SITE_URL}/og/ridalo-share-horizontal-1200x630.png`;

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

/**
 * True when the HTML served for THIS exact address already carries its final canonical / robots / hreflang
 * (listing pages whose English version is indexed: the Cloudflare Function writes them and a marker meta naming
 * the path). Then the page keeps them instead of recomputing the French-only defaults below.
 */
const serverOwnsHead = () => {
  try {
    const m = document.head.querySelector('meta[name="ridalo-seo-listing"]');
    return !!m && m.getAttribute('content') === window.location.pathname.replace(/\/+$/, '');
  } catch (e) { return false; }
};

export const SEO = ({
  title,
  description,
  canonical,
  noindex = false,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  jsonLd,
}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isEn = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en');
  const finalTitle = title || t('seo.default.title');
  const finalDescription = description || t('seo.default.description');
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    document.title = finalTitle;
    upsertMeta('name', 'description', finalDescription);

    // Canonical: one URL per page, without query string or trailing slash.
    // A static page published in English (/en/faq…) is its own canonical;
    // any other /en page points to its French URL and is not indexed.
    const path = location.pathname.length > 1 ? location.pathname.replace(/\/+$/, '') : '/';
    const urlLang = currentUrlLang();
    const publishedEn = urlLang === 'en' && isPublishedInEnglish(path);
    const canonicalUrl = publishedEn ? absoluteUrl('en', path) : (canonical || `${SITE_URL}${path === '/' ? '/' : path}`);
    let linkCanonical = document.head.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    const owned = serverOwnsHead();
    if (!owned) linkCanonical.setAttribute('href', canonicalUrl);

    const metaRobots = document.head.querySelector('meta[name="robots"]');
    const notIndexed = noindex || (urlLang === 'en' && !publishedEn);
    if (owned) {
      // canonical, robots and hreflang of the served HTML stay as they are
    } else if (notIndexed) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    } else if (metaRobots) {
      metaRobots.remove();
    }

    // hreflang fr / en / x-default: only between the two versions of a published page.
    if (!owned) document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
    if (!owned && !notIndexed) {
      hreflangLinks(path).forEach(({ hreflang, href }) => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'alternate');
        l.setAttribute('hreflang', hreflang);
        l.setAttribute('href', href);
        document.head.appendChild(l);
      });
    }

    upsertMeta('property', 'og:title', finalTitle);
    upsertMeta('property', 'og:description', finalDescription);
    if (!owned) upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:locale', isEn ? 'en_US' : 'fr_FR');
    upsertMeta('property', 'og:locale:alternate', isEn ? 'fr_FR' : 'en_US');
    upsertMeta('name', 'twitter:title', finalTitle);
    upsertMeta('name', 'twitter:description', finalDescription);
    upsertMeta('name', 'twitter:image', ogImage);

    // Page-level structured data (site-level Organization/WebSite live in index.html).
    document.head.querySelectorAll('script[data-seo-jsonld="page"]').forEach((s) => s.remove());
    if (jsonLdText) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', 'page');
      s.text = jsonLdText;
      document.head.appendChild(s);
    }
    return () => {
      document.head.querySelectorAll('script[data-seo-jsonld="page"]').forEach((s) => s.remove());
    };
  }, [finalTitle, finalDescription, canonical, noindex, location.pathname, ogImage, ogType, isEn, jsonLdText]);

  return null; // This component doesn't render anything
};

/** BreadcrumbList JSON-LD from [{ name, path }] (path relative to the site;
 *  on an English URL the items point to the English pages). */
export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: absoluteUrl(currentUrlLang() || 'fr', it.path),
  })),
});
